import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MailPlusIcon, CopyIcon, EllipsisVerticalIcon, PencilIcon, RefreshCwIcon, Trash2Icon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { useFetch } from '@/hooks/useFecth';
import { useSearchTextInput } from '@/hooks/useSearchTextInput';
import { cn } from '@/lib/utils';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import { useClubConfirm } from '../providers/ClubConfirmProvider';

const roles = [
    { label: 'Administrador', value: 'admin' },
    { label: 'Gerente', value: 'manager' },
    { label: 'Usuário', value: 'user' },
];

const ROLE_ITEMS = Object.fromEntries(roles.map(role => [role.value, role.label]));

function resolveInviteUrl(invite) {
    if (invite?.inviteUrl) return invite.inviteUrl;
    if (!invite?.token || typeof window === 'undefined') return '';
    return `${window.location.origin}/clube/convite/${encodeURIComponent(invite.token)}`;
}

const inviteSchema = z.object({
    email: z.string().email('Informe um e-mail válido').min(1, 'Informe o e-mail'),
    role: z.string().min(1),
});

const editMemberSchema = z.object({
    role: z.string().min(1),
});

function InviteDialog({ open, onOpenChange, onSuccess }) {
    const { setErrors } = useClubErrors();
    const form = useForm({
        resolver: zodResolver(inviteSchema),
        defaultValues: { email: '', role: 'user' },
    });

    useEffect(() => {
        if (open) form.reset({ email: '', role: 'user' });
    }, [open, form]);

    const onSubmit = async values => {
        try {
            await server.post('club/members/invites', {
                email: values.email.trim(),
                role: values.role,
            });
            await onSuccess();
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const { errors, isSubmitting } = form.formState;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Convidar membro</DialogTitle>
                </DialogHeader>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
                    <FieldGroup>
                        <Field data-invalid={Boolean(errors.email)}>
                            <FieldLabel htmlFor="invite-email">E-mail</FieldLabel>
                            <Controller
                                name="email"
                                control={form.control}
                                render={({ field }) => (
                                    <Input
                                        id="invite-email"
                                        type="email"
                                        aria-invalid={Boolean(errors.email)}
                                        {...field}
                                        value={field.value ?? ''}
                                    />
                                )}
                            />
                            <FieldError>{errors.email?.message}</FieldError>
                            <p className="text-xs text-muted-foreground">
                                Enviamos um link com validade limitada. O convidado cria a conta ou entra para
                                aceitar.
                            </p>
                        </Field>

                        <Field>
                            <FieldLabel>Cargo</FieldLabel>
                            <Controller
                                name="role"
                                control={form.control}
                                render={({ field }) => (
                                    <Select
                                        items={ROLE_ITEMS}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Selecione o cargo" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {roles.map(role => (
                                                <SelectItem key={role.value} value={role.value}>
                                                    {role.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </Field>
                    </FieldGroup>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner className="size-4" /> : 'Enviar convite'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function EditMemberDialog({ open, onOpenChange, assignment, onSuccess }) {
    const { setErrors } = useClubErrors();
    const form = useForm({
        resolver: zodResolver(editMemberSchema),
        defaultValues: { role: 'user' },
    });

    useEffect(() => {
        if (!open) return;
        form.reset({ role: assignment?.role || 'user' });
    }, [open, assignment, form]);

    const onSubmit = async values => {
        try {
            await server.put(`club/members/${assignment.id}`, { role: values.role });
            await onSuccess();
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const { isSubmitting } = form.formState;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Editar cargo</DialogTitle>
                </DialogHeader>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
                    <FieldGroup>
                        <Field>
                            <FieldLabel>E-mail</FieldLabel>
                            <Input value={assignment?.user?.userName || ''} disabled />
                        </Field>
                        <Field>
                            <FieldLabel>Cargo</FieldLabel>
                            <Controller
                                name="role"
                                control={form.control}
                                render={({ field }) => (
                                    <Select
                                        items={ROLE_ITEMS}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Selecione o cargo" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {roles.map(role => (
                                                <SelectItem key={role.value} value={role.value}>
                                                    {role.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </Field>
                    </FieldGroup>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function ListClubMembers() {
    const { user } = useAuth();
    const isOwner = user?.role === 'owner';
    const clubPortalContainer =
        typeof document !== 'undefined' ? document.getElementById('club-root') : null;
    const { search, input, handleChange, handleReset } = useSearchTextInput();
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [page, setPage] = useState(0);
    const confirm = useClubConfirm();
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const perPage = 10;

    const memberParams = useMemo(
        () => ({
            params: {
                page,
                perPage,
                search: search || undefined,
            },
        }),
        [page, perPage, search],
    );

    const { data: listResponse, mutate, isFetching } = useFetch('club/members', memberParams);
    const { data: invites, mutate: mutateInvites } = useFetch('club/members/invites', {
        params: { status: 'pending' },
    });

    const members = listResponse?.data ?? [];
    const pendingInvites = Array.isArray(invites) ? invites : invites?.data ?? [];
    const total = listResponse?.total ?? 0;
    const pages = listResponse?.pages ?? 1;

    useEffect(() => {
        setPage(0);
    }, [search]);

    const handleDelete = async member => {
        try {
            await confirm({
                title: 'Remover membro',
                description:
                    'Tem certeza que deseja remover este usuário do clube? Ele perderá o acesso à administração do clube.',
                confirmationText: 'Remover',
                confirmationButtonProps: { variant: 'destructive' },
            });

            await server.delete(`club/members/${member.id}`);
            await mutate();
            enqueueSnackbar?.('Membro removido.');
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const handleRevokeInvite = async invite => {
        try {
            await confirm({
                title: 'Revogar convite',
                description: `Revogar o convite para ${invite.email}?`,
                confirmationText: 'Revogar',
                confirmationButtonProps: { variant: 'destructive' },
            });
            await server.delete(`club/members/invites/${invite.id}`);
            await mutateInvites();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const handleResendInvite = async invite => {
        try {
            await server.post(`club/members/invites/${invite.id}/resend`);
            await mutateInvites();
            enqueueSnackbar?.('Convite reenviado.');
        } catch (error) {
            setErrors(error);
        }
    };

    const handleCopyInviteLink = async invite => {
        const url = resolveInviteUrl(invite);
        if (!url) {
            enqueueSnackbar?.('Link do convite indisponível.', { variant: 'error' });
            return;
        }
        try {
            await navigator.clipboard.writeText(url);
            enqueueSnackbar?.('Link do convite copiado.');
        } catch {
            enqueueSnackbar?.('Não foi possível copiar o link.', { variant: 'error' });
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Usuários do clube</h1>
                    <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                        Convide por e-mail. O convidado aceita o link, cria a conta ou entra com a senha já
                        existente. Para tirar o acesso, remova o membro do clube.
                    </p>
                </div>
                {isOwner ? (
                    <Button onClick={() => setInviteOpen(true)}>
                        <MailPlusIcon className="size-4" />
                        Convidar
                    </Button>
                ) : null}
            </div>

            <Tabs defaultValue="members">
                <TabsList>
                    <TabsTrigger value="members">Membros</TabsTrigger>
                    <TabsTrigger value="invites">
                        Convites
                        {pendingInvites.length > 0 ? (
                            <Badge variant="secondary" className="ml-2">
                                {pendingInvites.length}
                            </Badge>
                        ) : null}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="members" className="mt-4 flex flex-col gap-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Buscar</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Buscar por e-mail"
                                    value={input}
                                    onChange={event => handleChange(event.target.value)}
                                    className="flex-1"
                                />
                                {input ? (
                                    <Button type="button" variant="outline" onClick={handleReset}>
                                        Limpar
                                    </Button>
                                ) : null}
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Membros</CardTitle>
                            <CardDescription>{total} membro(s)</CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b bg-muted/40 text-left">
                                            <th className="px-4 py-3 font-medium">E-mail</th>
                                            <th className="px-4 py-3 font-medium">Cargo</th>
                                            <th className="px-4 py-3 text-right font-medium">Desde</th>
                                            <th className="px-4 py-3 text-right font-medium">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.map(item => {
                                            const isMemberOwner = item.role === 'owner';
                                            return (
                                                <tr key={item.id} className="border-b last:border-b-0">
                                                    <td className="px-4 py-3">{item.user?.userName}</td>
                                                    <td className="px-4 py-3">
                                                        {isMemberOwner
                                                            ? 'Proprietário'
                                                            : (roles.find(role => role.value === item.role)?.label ??
                                                              item.role)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-muted-foreground">
                                                        {item.user?.createdAt
                                                            ? new Date(item.user.createdAt).toLocaleDateString(
                                                                  'pt-BR',
                                                              )
                                                            : '--'}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex justify-end gap-1">
                                                            {isOwner && !isMemberOwner ? (
                                                                <>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon-sm"
                                                                        aria-label="Editar cargo"
                                                                        onClick={() => setEditing(item)}
                                                                    >
                                                                        <PencilIcon className="size-4" />
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon-sm"
                                                                        aria-label="Remover"
                                                                        onClick={() => handleDelete(item)}
                                                                    >
                                                                        <Trash2Icon className="size-4 text-destructive" />
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                <span className="px-2 text-xs text-muted-foreground">
                                                                    —
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {members.length === 0 && !isFetching ? (
                                <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                                    Nenhum membro cadastrado.
                                </p>
                            ) : null}

                            {pages > 1 ? (
                                <div className="flex items-center justify-center gap-3 border-t px-4 py-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={page <= 0}
                                        onClick={() => setPage(current => Math.max(0, current - 1))}
                                    >
                                        Anterior
                                    </Button>
                                    <span className="text-sm text-muted-foreground">
                                        Página {page + 1} de {pages}
                                    </span>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={page >= pages - 1}
                                        onClick={() => setPage(current => current + 1)}
                                    >
                                        Próxima
                                    </Button>
                                </div>
                            ) : null}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="invites" className="mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Convites pendentes</CardTitle>
                            <CardDescription>
                                Links enviados por e-mail. Você pode copiar, reenviar ou revogar.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b bg-muted/40 text-left">
                                            <th className="px-4 py-3 font-medium">E-mail</th>
                                            <th className="px-4 py-3 font-medium">Cargo</th>
                                            <th className="px-4 py-3 font-medium">Expira em</th>
                                            <th className="px-4 py-3 text-right font-medium">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingInvites.map(invite => (
                                            <tr key={invite.id} className="border-b last:border-b-0">
                                                <td className="px-4 py-3">{invite.email}</td>
                                                <td className="px-4 py-3">
                                                    {roles.find(r => r.value === invite.role)?.label ??
                                                        invite.role}
                                                </td>
                                                <td className="px-4 py-3 text-muted-foreground">
                                                    {invite.expiresAt
                                                        ? new Date(invite.expiresAt).toLocaleString('pt-BR')
                                                        : '--'}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex justify-end gap-2">
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleCopyInviteLink(invite)}
                                                        >
                                                            <CopyIcon className="size-4" />
                                                            Copiar link
                                                        </Button>
                                                        {isOwner ? (
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger
                                                                    render={
                                                                        <button
                                                                            type="button"
                                                                            className={cn(
                                                                                buttonVariants({
                                                                                    variant: 'outline',
                                                                                    size: 'icon-sm',
                                                                                }),
                                                                            )}
                                                                            aria-label="Mais ações"
                                                                        />
                                                                    }
                                                                >
                                                                    <EllipsisVerticalIcon className="size-4" />
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent
                                                                    container={clubPortalContainer}
                                                                    align="end"
                                                                    className="min-w-40"
                                                                >
                                                                    <DropdownMenuItem
                                                                        onClick={() =>
                                                                            handleResendInvite(invite)
                                                                        }
                                                                    >
                                                                        <RefreshCwIcon />
                                                                        Reenviar
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        variant="destructive"
                                                                        onClick={() =>
                                                                            handleRevokeInvite(invite)
                                                                        }
                                                                    >
                                                                        <Trash2Icon />
                                                                        Excluir
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        ) : null}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {pendingInvites.length === 0 ? (
                                <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                                    Nenhum convite pendente.
                                </p>
                            ) : null}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <InviteDialog
                open={inviteOpen}
                onOpenChange={setInviteOpen}
                onSuccess={async () => {
                    await mutateInvites();
                    enqueueSnackbar?.('Convite enviado.');
                }}
            />
            <EditMemberDialog
                open={Boolean(editing)}
                onOpenChange={isOpen => !isOpen && setEditing(null)}
                assignment={editing}
                onSuccess={() => mutate()}
            />
        </div>
    );
}
